import { pb } from "@/lib/pocketbase";
import { ShopifyBlog } from "@/types/integration";

export interface ShopifyConnectionTestRequest {
  brandId: string;
  url: string;
  accessToken: string;
}

export interface ShopifyConnectionRequest extends ShopifyConnectionTestRequest {
  defaultBlogId?: string;
  autoPublish?: boolean;
  defaultAuthor?: string;
  defaultTags?: string;
}

export interface ShopifyUpdateSettingsRequest {
  brandId: string;
  defaultBlogId?: string;
  autoPublish?: boolean;
  defaultAuthor?: string;
  defaultTags?: string;
}

export interface ShopifyTestResponse {
  message: string;
  blogs: ShopifyBlog[];
}

export interface ShopifyBlogsResponse {
  blogs: ShopifyBlog[];
  currentDefaultBlogId?: string;
  autoPublish?: boolean;
}

export interface ShopifyConnectionResponse {
  message: string;
  defaultBlogId: string;
  autoPublish: boolean;
}

export interface ShopifySettingsResponse {
  message: string;
  settings: {
    defaultBlogId?: string;
    autoPublish?: boolean;
    defaultAuthor?: string;
    defaultTags?: string;
  };
}

class ShopifyService {
  private baseURL = pb.baseURL;

  async testConnection(
    request: ShopifyConnectionTestRequest,
  ): Promise<ShopifyTestResponse> {
    const response = await fetch(
      `${this.baseURL}/api/v1/shopify/test-connection`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to test Shopify connection");
    }

    return response.json();
  }

  async connect(
    request: ShopifyConnectionRequest,
  ): Promise<ShopifyConnectionResponse> {
    const response = await fetch(`${this.baseURL}/api/v1/shopify/connect`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to connect to Shopify");
    }

    return response.json();
  }

  async getBlogs(brandId: string): Promise<ShopifyBlogsResponse> {
    const response = await fetch(`${this.baseURL}/api/v1/shopify/get-blogs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify({ brandId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get Shopify blogs");
    }

    return response.json();
  }

  async updateSettings(
    request: ShopifyUpdateSettingsRequest,
  ): Promise<ShopifySettingsResponse> {
    const response = await fetch(
      `${this.baseURL}/api/v1/shopify/update-settings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update Shopify settings");
    }

    return response.json();
  }

  async publish(data: {
    postId: string;
    brandId: string;
    blogId?: string; // Optional override
  }): Promise<{ article_id: string; link: string }> {
    const response = await fetch(`${this.baseURL}/api/v1/shopify/publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to publish to Shopify");
    }

    return response.json();
  }
}

export const shopifyService = new ShopifyService();
